import type { HelpGuide } from '../types';

export const settingsCoaGuide: HelpGuide = {
  key: 'settings-coa',
  title: 'ตั้งค่าผังบัญชี (Account Mapping)',
  subtitle: 'เมนู: ระบบ ▸ ตั้งค่าผังบัญชี',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/settings-coa/คู่มือการใช้งาน_ตั้งค่าผังบัญชี_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “ตั้งค่าผังบัญชี (เปิด/ปิดการใช้งาน)” ใช้ดูผังบัญชี (Chart of Accounts) ของบริษัทที่เลือก และ เปิด/ปิดการใช้งาน บัญชีแต่ละรายการ บัญชีที่ “เปิดใช้งาน” จะเลือกได้ตอนลงรายการบัญชีในระบบ ส่วนบัญชีที่ “ปิด” จะถูกซ่อนไม่ให้ใช้งานต่อ (เป็นการปิดแบบ soft-delete ไม่ได้ลบทิ้งจริง)',
        },
        {
          kind: 'callout',
          text: 'หลักการ: รหัสบัญชีและชื่อบัญชีในหน้านี้เป็นข้อมูลตั้งต้นของผังบัญชี ผู้ใช้แก้ไขในหน้านี้ไม่ได้ — สิ่งเดียวที่ทำได้คือ “เปิด/ปิดการใช้งาน” ของแต่ละบัญชี การเปิด/ปิดทำได้ทีละบัญชีด้วยช่องติ๊ก หรือทำพร้อมกันทุกบัญชีที่กรองอยู่ด้วยปุ่มดำเนินการหมู่',
          variant: 'info',
        },
        { kind: 'para', text: 'สิ่งที่ทำได้ในหน้านี้:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ดูผังบัญชีทั้งหมด — ',
              text: 'รายการบัญชีของบริษัทที่เลือก จัดกลุ่มตามกลุ่มหลัก/กลุ่มย่อย',
            },
            { lead: 'ดูสรุปจำนวน — ', text: 'การ์ดสรุปบัญชีทั้งหมด / เปิดใช้งาน / ปิดใช้งาน' },
            { lead: 'ค้นหา — ', text: 'ค้นหาด้วยรหัสบัญชีหรือชื่อบัญชี (ภาษาไทย)' },
            { lead: 'เปิด/ปิดทีละบัญชี — ', text: 'ติ๊กช่อง “การใช้งาน” ในแถวเพื่อสลับสถานะ' },
            {
              lead: 'เปิด/ปิดแบบหมู่ — ',
              text: 'ปุ่ม เปิดใช้งานที่แสดง / ปิดใช้งานที่แสดง สำหรับทุกบัญชีที่กรองอยู่',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/settings-coa/01-overview.png',
          caption:
            'ภาพรวมหน้าตั้งค่าผังบัญชี — การ์ดสรุป + ตารางผังบัญชีจัดกลุ่มตามกลุ่มหลัก/กลุ่มย่อย',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'การ์ดสรุปยอด (KPI) ด้านบน 3 ใบ:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'บัญชีทั้งหมด — ',
              text: 'จำนวนบัญชีทั้งหมดของบริษัทที่เลือก (รวมที่เปิดและที่ปิด)',
            },
            { lead: 'เปิดใช้งาน — ', text: 'จำนวนบัญชีที่สถานะใช้งานอยู่' },
            { lead: 'ปิดใช้งาน — ', text: 'จำนวนบัญชีที่ถูกปิด' },
          ],
        },
        {
          kind: 'para',
          text: 'ถัดลงมามีแถบเครื่องมือ: ช่องค้นหา (ค้นหารหัสบัญชีหรือชื่อบัญชี) · ปุ่ม รีเซ็ต (ล้างคำค้น) · ป้ายแสดงบริษัทปัจจุบัน (บริษัท: …) ส่วนปุ่มดำเนินการหมู่อยู่ที่ส่วนหัว: เปิดใช้งานที่แสดง และ ปิดใช้งานที่แสดง',
        },
        {
          kind: 'para',
          text: 'ตารางผังบัญชีมี 4 คอลัมน์: รหัสบัญชี · ชื่อบัญชี · ประเภท · การใช้งาน แถวจะถูกจัดกลุ่มด้วยหัวข้อกลุ่มหลัก (เช่น สินทรัพย์ ต้นทุน) และมีหัวข้อกลุ่มย่อยคั่นเมื่อกลุ่มนั้นมีหลายกลุ่มย่อย ด้านล่างตารางมีบรรทัดสรุป “แสดง … จาก … บัญชี”',
        },
        {
          kind: 'image',
          src: '/help/settings-coa/02-kpi-cards.png',
          caption: 'การ์ดสรุป บัญชีทั้งหมด / เปิดใช้งาน / ปิดใช้งาน และปุ่ม เปิดใช้งานที่แสดง',
        },
      ],
    },
    {
      id: 'search',
      heading: '3. การค้นหาบัญชี',
      blocks: [
        {
          kind: 'para',
          text: 'พิมพ์ในช่อง ค้นหารหัสบัญชีหรือชื่อบัญชี เพื่อกรองรายการ ระบบจะค้นหาทั้งจากรหัสบัญชีและชื่อบัญชีภาษาไทย (ไม่สนตัวพิมพ์เล็ก/ใหญ่) ตารางจะแสดงเฉพาะบัญชีที่ตรงเงื่อนไข พร้อมอัปเดตจำนวนที่แสดงด้านล่าง',
        },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ค้นด้วยชื่อ — ',
              text: 'เช่น พิมพ์ “ภาษี” เพื่อกรองเฉพาะบัญชีที่ชื่อมีคำว่าภาษี',
            },
            { lead: 'ค้นด้วยรหัส — ', text: 'พิมพ์เลขรหัสบางส่วน เช่น “1” เพื่อกรองตามรหัสบัญชี' },
            {
              lead: 'รีเซ็ต — ',
              text: 'กดปุ่ม รีเซ็ต เพื่อล้างคำค้นและกลับมาแสดงทั้งหมด (กดได้เมื่อมีคำค้น)',
            },
          ],
        },
        {
          kind: 'callout',
          text: 'ถ้าไม่มีบัญชีตรงกับคำค้น ตารางจะแสดงข้อความ “ไม่พบบัญชีที่ตรงกับเงื่อนไข” ให้ลองแก้คำค้นหรือกด รีเซ็ต',
          variant: 'info',
        },
        {
          kind: 'image',
          src: '/help/settings-coa/03-search-filter.png',
          caption: 'การค้นหาบัญชีด้วยคำว่า “ภาษี” — ตารางกรองเฉพาะบัญชีที่ตรงเงื่อนไข',
        },
      ],
    },
    {
      id: 'grouped',
      heading: '4. มุมมองจัดกลุ่มตามกลุ่มหลัก/กลุ่มย่อย',
      blocks: [
        {
          kind: 'para',
          text: 'ตารางจัดเรียงบัญชีเป็นลำดับชั้น โดยมีหัวข้อ กลุ่มหลัก (majorGroup) คั่นแต่ละหมวด เช่น สินทรัพย์ ต้นทุน และมีหัวข้อ กลุ่มย่อย คั่นเพิ่มเมื่อกลุ่มหลักนั้นแบ่งเป็นหลายกลุ่มย่อย (เช่น “ต้นทุนขายตรง”) ทำให้มองเห็นโครงสร้างผังบัญชีตามงบการเงินได้ชัดเจน',
        },
        {
          kind: 'para',
          text: 'การจัดกลุ่มนี้เป็นการแสดงผลเพื่อการอ่านงบเท่านั้น ทุกกลุ่มแสดงตลอด (ไม่มีการย่อ/ขยายกลุ่ม) และยังคงโครงสร้างเดิมแม้จะค้นหาหรือกรองรายการ',
        },
        {
          kind: 'image',
          src: '/help/settings-coa/04-grouped-view.png',
          caption: 'มุมมองจัดกลุ่ม — หัวข้อกลุ่มหลักและกลุ่มย่อยคั่นบัญชีในแต่ละหมวด',
        },
      ],
    },
    {
      id: 'usage-toggle',
      heading: '5. การเปิด/ปิดการใช้งานทีละบัญชี',
      blocks: [
        {
          kind: 'para',
          text: 'คอลัมน์ การใช้งาน ในแต่ละแถวมีช่องติ๊กพร้อมป้ายสถานะ ติ๊กช่องเพื่อ “เปิดใช้งาน” บัญชี (ป้ายเปลี่ยนเป็น ใช้งาน พร้อมจุดสีเขียว) หรือเอาเครื่องหมายออกเพื่อ “ปิด” (ป้ายเป็น ปิด สีเทา) ระบบบันทึกการเปลี่ยนสถานะทันที และการ์ดสรุปด้านบนจะนับใหม่ให้อัตโนมัติ',
        },
        {
          kind: 'callout',
          text: 'การเปลี่ยนสถานะต้องมีสิทธิ์ coa.manage หากบัญชียังไม่มีในระบบ (ไม่มีรหัสอ้างอิง) หรือกำลังบันทึกอยู่ ช่องติ๊กจะกดไม่ได้ชั่วคราว',
          variant: 'warn',
        },
        {
          kind: 'image',
          src: '/help/settings-coa/05-usage-toggle.png',
          caption: 'คอลัมน์การใช้งาน — ช่องติ๊กและป้ายสถานะ ใช้งาน / ปิด ในแต่ละแถว',
        },
      ],
    },
    {
      id: 'bulk',
      heading: '6. การเปิด/ปิดแบบหมู่ และบรรทัดสรุป',
      blocks: [
        {
          kind: 'para',
          text: 'ปุ่ม เปิดใช้งานที่แสดง และ ปิดใช้งานที่แสดง ที่ส่วนหัว จะดำเนินการกับ ทุกบัญชีที่กรองอยู่ในตอนนั้น เมื่อกดจะมีกล่องยืนยันแจ้งจำนวนบัญชี เช่น “เปิดใช้งานบัญชีที่แสดงอยู่ 5 บัญชี?” กดตกลงเพื่อสลับสถานะทั้งหมดในครั้งเดียว',
        },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ใช้คู่กับการค้นหา — ',
              text: 'กรองรายการที่ต้องการก่อน แล้วใช้ปุ่มหมู่เพื่อเปิด/ปิดเฉพาะกลุ่มนั้น',
            },
            {
              lead: 'ปุ่มถูกปิดเมื่อ — ',
              text: 'ไม่ได้เลือกบริษัท · ไม่มีบัญชีที่กรองอยู่ · หรือกำลังบันทึกอยู่',
            },
            {
              lead: 'บรรทัดสรุป — ',
              text: 'ใต้ตารางแสดง “แสดง {จำนวนที่กรอง} จาก {จำนวนทั้งหมด} บัญชี”',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/settings-coa/06-footer-bulk.png',
          caption: 'กลุ่มสินทรัพย์/หนี้สิน และบรรทัดสรุปจำนวนบัญชีที่แสดงด้านล่างตาราง',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '7. สรุปสถานะการใช้งาน',
      blocks: [
        {
          kind: 'table',
          head: ['ป้ายสถานะ', 'ความหมาย'],
          rows: [
            ['ใช้งาน', 'บัญชีเปิดใช้งานอยู่ — เลือกใช้ได้ตอนลงรายการบัญชี (ป้ายเขียว มีจุด)'],
            ['ปิด', 'บัญชีถูกปิด — ไม่นำมาใช้งานต่อ (ป้ายเทา เป็นการปิดแบบไม่ลบทิ้ง)'],
          ],
        },
        {
          kind: 'para',
          text: 'ประเภทบัญชีในคอลัมน์ ประเภท แสดงตามหมวด: สินทรัพย์ · หนี้สิน · ส่วนของผู้ถือหุ้น · รายได้ · ต้นทุน · ค่าใช้จ่าย',
          tone: 'muted',
        },
      ],
    },
    {
      id: 'faq',
      heading: '8. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'แก้ชื่อหรือรหัสบัญชีในหน้านี้ได้ไหม?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ไม่ได้ — หน้านี้ทำได้แค่เปิด/ปิดการใช้งาน รหัสและชื่อบัญชีเป็นข้อมูลตั้งต้นของผังบัญชี',
            },
          ],
        },
        { kind: 'para', text: 'ปิดบัญชีแล้วข้อมูลหายไหม?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ไม่หาย — การปิดเป็นแบบ soft-delete เพียงซ่อนไม่ให้เลือกใช้งานต่อ เปิดกลับได้ทุกเมื่อ',
            },
          ],
        },
        { kind: 'para', text: 'ทำไมปุ่มเปิด/ปิดที่แสดงกดไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ปุ่มจะกดไม่ได้เมื่อยังไม่ได้เลือกบริษัท ไม่มีบัญชีที่กรองอยู่ หรือระบบกำลังบันทึกอยู่',
            },
          ],
        },
        { kind: 'para', text: 'หน้านี้แสดงบัญชีของบริษัทไหน?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'แสดงตามบริษัทที่เลือกอยู่ (ดูที่ป้าย บริษัท: …) ผังบัญชีแยกตามบริษัท (DCC/TTOP/RCI)',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '9. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ผังบัญชี (Chart of Accounts) — ',
              text: 'รายการบัญชีทั้งหมดของบริษัท พร้อมรหัส ชื่อ และประเภท',
            },
            {
              lead: 'รหัสบัญชี — ',
              text: 'เลขรหัสประจำบัญชี (ตามแบบ 8 หลัก) ใช้อ้างอิงในการลงบัญชี',
            },
            {
              lead: 'ประเภทบัญชี — ',
              text: 'หมวดของบัญชี: สินทรัพย์ / หนี้สิน / ส่วนของผู้ถือหุ้น / รายได้ / ต้นทุน / ค่าใช้จ่าย',
            },
            {
              lead: 'กลุ่มหลัก / กลุ่มย่อย — ',
              text: 'การจัดกลุ่มบัญชีเพื่อแสดงผลและจัดทำงบการเงิน',
            },
            {
              lead: 'การใช้งาน (isActive) — ',
              text: 'สถานะเปิด/ปิดของบัญชี ปิดแล้วซ่อนจากการเลือกใช้งาน (soft-delete)',
            },
            {
              lead: 'ดำเนินการหมู่ — ',
              text: 'การเปิด/ปิดทุกบัญชีที่กรองอยู่พร้อมกันด้วยปุ่มเดียว',
            },
          ],
        },
      ],
    },
  ],
};
